import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import doctorService from '../services/doctorService';

const DoctorAvailabilityPage = () => {
  const navigate = useNavigate();
  
  // Set page title
  useEffect(() => {
    document.title = 'MediFusion - Doctor Availability';
  }, []);

  // State for data
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTimeSlots, setSelectedTimeSlots] = useState([]);
  const [workingHours, setWorkingHours] = useState(''); // To display the preset time range
  const [unavailabilityReasons, setUnavailabilityReasons] = useState({
    sickLeave: false,
    annualLeave: false,
    other: false
  });
  const [otherReason, setOtherReason] = useState('');
  const [doctorId, setDoctorId] = useState(null);
  const [monthOverview, setMonthOverview] = useState({ days: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Function to calculate working hours from time slots
  const calculateWorkingHours = (slots) => {
    if (!slots || slots.length === 0) {
      return '';
    }

    // Find the earliest start time and latest end time from available slots
    const availableSlots = slots.filter(slot => 
      (slot.status === 'available' || slot.status === 'selected') &&
      slot.start_time && slot.end_time &&
      typeof slot.start_time === 'string' && typeof slot.end_time === 'string'
    );
    if (availableSlots.length === 0) {
      return 'No available slots';
    }

    const startTimes = availableSlots.map(slot => slot.start_time).filter(time => time);
    const endTimes = availableSlots.map(slot => slot.end_time).filter(time => time);
    
    if (startTimes.length === 0 || endTimes.length === 0) {
      return 'No valid time slots';
    }
    
    const earliestStart = startTimes.sort()[0];
    const latestEnd = endTimes.sort().reverse()[0];

      // Convert 24-hour format to 12-hour format for display
  const formatTime = (time) => {
    if (!time || typeof time !== 'string') {
      return 'Invalid time';
    }
    const parts = time.split(':');
    if (parts.length < 2) {
      return 'Invalid time';
    }
    const [hours, minutes] = parts;
    const hour = parseInt(hours);
    if (isNaN(hour)) {
      return 'Invalid time';
    }
    const period = hour >= 12 ? 'pm' : 'am';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes || '00'}${period}`;
  };

    return `${formatTime(earliestStart)} - ${formatTime(latestEnd)}`;
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Helpers
  const toIsoDate = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const parseApiSlotsToUi = (apiSlots) => {
    // apiSlots: [{id, start_time, end_time, status}]
    if (!apiSlots || !Array.isArray(apiSlots)) {
      return [];
    }
    
    return apiSlots
      .filter(s => s && s.start_time && s.end_time) // Filter out invalid slots
      .map((s) => {
        const start = s.start_time?.slice(0, 5) || '';
        const end = s.end_time?.slice(0, 5) || '';
        const status = s.status === 'AVAILABLE' ? 'available' : s.status === 'BOOKED' ? 'booked' : 'unavailable';
        return { 
          id: s.id, 
          start: `${start}`, 
          end: `${end}`, 
          status,
          start_time: s.start_time,
          end_time: s.end_time
        };
      });
  };

  const filterPastSlotsForToday = (slots, selectedDate) => {
    // Only filter if the selected date is today
    const today = new Date();
    const selected = new Date(selectedDate);
    
    // Check if selected date is today (compare date only)
    const isToday = today.toDateString() === selected.toDateString();
    
    if (!isToday) {
      return slots; // Return all slots for non-today dates
    }
    
    // For today, filter out past time slots
    const currentTime = new Date();
    const currentHour = currentTime.getHours();
    const currentMinute = currentTime.getMinutes();
    
    return slots.filter(slot => {
      if (!slot.start) return false;
      
      const [slotHour, slotMinute] = slot.start.split(':').map(Number);
      const slotTimeInMinutes = slotHour * 60 + slotMinute;
      const currentTimeInMinutes = currentHour * 60 + currentMinute;
      
      // Keep slots that start after current time
      return slotTimeInMinutes > currentTimeInMinutes;
    });
  };

  const reasonKeyToCode = (key) => {
    if (key === 'sickLeave') return 'SICK';
    if (key === 'annualLeave') return 'ANNUAL';
    if (key === 'other') return 'OTHER';
    return null;
  };

  // Load doctor, month overview, and today's availability
  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        const user = doctorService.getCurrentUser();
        if (!user) {
          throw new Error('User not authenticated');
        }
        const doctor = await doctorService.getDoctorByUserId(user.id);
        setDoctorId(doctor.id);
        
        // Load month overview
        const overview = await doctorService.getMonthAvailability(doctor.id, selectedYear, selectedMonth + 1);
        setMonthOverview(overview);
        
        // Load today's availability by default
        const today = new Date();
        setSelectedDate(today);
        const todayIso = toIsoDate(today);
        
        try {
          let slots = await doctorService.getDaySlots(doctor.id, todayIso);
          if (!slots || slots.length === 0) {
            // Initialize today with default 15-min slots 09:00-17:00 if no slots exist
            await doctorService.bulkSetAvailability(doctor.id, {
              dates: [todayIso],
              is_available: true,
              preset_from: '09:00',
              preset_to: '17:00',
              slot_minutes: 15,
            });
            slots = await doctorService.getDaySlots(doctor.id, todayIso);
            // Refresh month overview to mark today as available
            const updatedOverview = await doctorService.getMonthAvailability(doctor.id, selectedYear, selectedMonth + 1);
            setMonthOverview(updatedOverview);
          }
          const uiSlots = parseApiSlotsToUi(slots);
          const filteredSlots = filterPastSlotsForToday(uiSlots, today);
          setSelectedTimeSlots(filteredSlots);
          
          // Calculate and set working hours for today
          const hours = calculateWorkingHours(uiSlots);
          setWorkingHours(hours);
        } catch (slotError) {
          console.error('Error loading today\'s slots:', slotError);
          // Don't show error toast for slot loading issues, just continue
        }
        
      } catch (e) {
        setError(e.message || 'Failed to load availability');
        toast.error(e.message || 'Failed to load availability');
      } finally {
        setLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchOverview = async () => {
      if (!doctorId) return;
      try {
        setLoading(true);
        const overview = await doctorService.getMonthAvailability(doctorId, selectedYear, selectedMonth + 1);
        setMonthOverview(overview);
      } catch (e) {
        setError(e.message || 'Failed to load month');
        toast.error(e.message || 'Failed to load month');
      } finally {
        setLoading(false);
      }
    };
    fetchOverview();
  }, [doctorId, selectedYear, selectedMonth]);

  // Generate real calendar days for selected year and month (including weekends)
  const generateCalendarDays = () => {
    const year = selectedYear;
    const month = selectedMonth;
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // Find the first Sunday of the calendar grid (to show complete weeks)
    const startDate = new Date(firstDay);
    const firstDayOfWeek = firstDay.getDay();
    
    // Go back to the previous Sunday (0)
    if (firstDayOfWeek !== 0) {
      startDate.setDate(startDate.getDate() - firstDayOfWeek);
    }
    
    const days = [];
    const currentDate = new Date(startDate);
    
    // Generate 42 days (6 weeks * 7 days) to ensure we show complete weeks
    while (days.length < 42) {
      days.push({
        date: new Date(currentDate),
        isCurrentMonth: currentDate.getMonth() === month,
        day: currentDate.getDate()
      });
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return days;
  };

  // Available dates from API overview
  const getAvailableDates = () => {
    const today = new Date();
    today.setHours(0,0,0,0);
    const set = new Set();
    (monthOverview?.days || []).forEach((d) => {
      const dt = new Date(d.avail_date);
      dt.setHours(0,0,0,0);
      if (d.is_available && dt >= today) {
        set.add(dt.getDate());
      }
    });
    return Array.from(set);
  };

  // Get days with actual available slots (for calendar color coding)
  const getDaysWithAvailableSlots = () => {
    const today = new Date();
    today.setHours(0,0,0,0);
    const daysWithSlots = new Set();
    
    (monthOverview?.days || []).forEach((d) => {
      const dt = new Date(d.avail_date);
      dt.setHours(0,0,0,0);
      
      // Only include future dates that are available and have available slots
      if (dt >= today && d.is_available && d.available_slots_count > 0) {
        daysWithSlots.add(dt.getDate());
      }
    });
    
    return Array.from(daysWithSlots);
  };

  // Get days that are unavailable or have no available slots (for red background)
  const getUnavailableDays = () => {
    const today = new Date();
    today.setHours(0,0,0,0);
    const unavailableDays = new Set();
    
    (monthOverview?.days || []).forEach((d) => {
      const dt = new Date(d.avail_date);
      dt.setHours(0,0,0,0);
      
      // Include future dates that are either marked unavailable or have no available slots
      if (dt >= today && (!d.is_available || d.available_slots_count === 0)) {
        unavailableDays.add(dt.getDate());
      }
    });
    
    return Array.from(unavailableDays);
  };

  const calendarDays = generateCalendarDays();
  const availableDates = getAvailableDates();
  const daysWithAvailableSlots = getDaysWithAvailableSlots();
  const unavailableDays = getUnavailableDays();

  const handleYearChange = (direction) => {
    setSelectedYear(prev => direction === 'next' ? prev + 1 : prev - 1);
  };

  const handleMonthChange = (direction) => {
    setSelectedMonth(prev => {
      if (direction === 'next') {
        return prev === 11 ? 0 : prev + 1;
      } else {
        return prev === 0 ? 11 : prev - 1;
      }
    });
  };

  const handleDayClick = async (day) => {
    if (!day.isCurrentMonth || !doctorId) return;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDateOnly = new Date(day.date);
    selectedDateOnly.setHours(0, 0, 0, 0);
    setSelectedDate(day.date);
    if (selectedDateOnly < today) {
      setSelectedTimeSlots([]);
      setWorkingHours('');
      toast.error('Cannot select past dates');
      return;
    }
    try {
      setLoading(true);
      const iso = toIsoDate(selectedDateOnly);
      let slots = await doctorService.getDaySlots(doctorId, iso);
      if (!slots || slots.length === 0) {
        // Initialize day with default 15-min slots 09:00-17:00
        await doctorService.bulkSetAvailability(doctorId, {
          dates: [iso],
          is_available: true,
          preset_from: '09:00',
          preset_to: '17:00',
          slot_minutes: 15,
        });
        slots = await doctorService.getDaySlots(doctorId, iso);
        // refresh month overview to mark day available
        const overview = await doctorService.getMonthAvailability(doctorId, selectedYear, selectedMonth + 1);
        setMonthOverview(overview);
      }
      const uiSlots = parseApiSlotsToUi(slots);
      const filteredSlots = filterPastSlotsForToday(uiSlots, day.date);
      setSelectedTimeSlots(filteredSlots);
      
      // Calculate and set working hours
      const hours = calculateWorkingHours(uiSlots);
      setWorkingHours(hours);
    } catch (e) {
      setError(e.message || 'Failed to load day slots');
      toast.error(e.message || 'Failed to load day slots');
      setWorkingHours('');
    } finally {
      setLoading(false);
    }
  };

  const handleTimeSlotToggle = (slotId) => {
    setSelectedTimeSlots(prev => 
      prev.map(slot => {
        if (slot.id === slotId) {
          // Only allow toggling for available slots
          if (slot.status === 'available') {
            return { ...slot, status: 'selected' };
          } else if (slot.status === 'selected') {
            return { ...slot, status: 'available' };
          }
          // For booked or unavailable slots, don't allow selection
          return slot;
        }
        return slot;
      })
    );
  };

  const handleUnavailabilityChange = (reason) => {
    setUnavailabilityReasons(prev => {
      // Reset all checkboxes first
      const resetState = {
        sickLeave: false,
        annualLeave: false,
        other: false
      };
      // Set only the clicked checkbox to true
      resetState[reason] = !prev[reason];
      return resetState;
    });
  };

  const handleSubmit = async () => {
    if (!doctorId) return;
    if (!selectedDate) {
      toast.error('Select a date');
      return;
    }
    const iso = toIsoDate(selectedDate);
    const selected = selectedTimeSlots.filter(s => s.status === 'selected');
    const reasonKey = Object.keys(unavailabilityReasons).find(k => unavailabilityReasons[k]);
    try {
      setLoading(true);
      if (reasonKey) {
        const reason = reasonKeyToCode(reasonKey);
        await doctorService.setUnavailability(doctorId, [iso], reason);
        toast.success('Unavailability saved');
        setSelectedTimeSlots([]);
      } else if (selected.length > 0) {
        // Replace day slots to exactly these selections (as available)
        const slots = selected.map(s => ({ start_time: `${s.start}:00`.slice(0,5), end_time: `${s.end}:00`.slice(0,5) }));
        await doctorService.replaceDaySlots(doctorId, iso, slots);
        toast.success('Availability saved');
      } else {
        toast.error('Select at least one time slot or a reason');
        return;
      }
      // Refresh overview
      const overview = await doctorService.getMonthAvailability(doctorId, selectedYear, selectedMonth + 1);
      setMonthOverview(overview);
    } catch (e) {
      toast.error(e.message || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
                   {/* Top Banner */}
      <div className="bg-blue-600 text-white px-6 py-1 rounded-lg mb-6">
        <h1 className="text-base font-semibold text-center">Current Availability</h1>
      </div>

             {/* Main Content */}
       <div className="bg-white rounded-lg shadow-md p-6">
         <div className="flex gap-2">
                       {/* Column 1: Navigation Items (17%) */}
            <div className="w-[17%]">
              <div className="flex flex-col space-y-2">
                <button className="w-[80%] bg-blue-600 text-white py-2 px-3 rounded-lg font-medium text-sm text-center transform hover:scale-105 transition-all duration-200 shadow-md hover:shadow-lg">
                  Current Availability
                </button>
                <Link to="/doctor/availability/edit" className="w-[80%] bg-white text-blue-600 py-2 px-3 rounded-lg font-medium text-sm border border-gray-300 hover:bg-gray-50 transition-all duration-200 text-center transform hover:scale-105 shadow-sm hover:shadow-md">
                  Edit Availability
                </Link>
              </div>
            </div>

           {/* Column 2: Year Section (23%) */}
           <div className="w-[23%] -ml-4">
             <div className="bg-blue-600 text-white px-3 py-2 rounded-t-lg">
               <h3 className="font-semibold text-center text-sm">Year</h3>
             </div>
                           <div className="border border-gray-300 rounded-b-lg p-4">
                <div className="flex items-center justify-center space-x-4 mb-4">
                  <button 
                    onClick={() => handleYearChange('prev')}
                    className="text-blue-600 hover:text-blue-800 p-2 rounded-full hover:bg-blue-50 transition-colors"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <span className="font-bold text-gray-800 text-lg px-4 py-2 bg-gray-50 rounded-lg min-w-[80px] text-center">{selectedYear}</span>
                  <button 
                    onClick={() => handleYearChange('next')}
                    className="text-blue-600 hover:text-blue-800 p-2 rounded-full hover:bg-blue-50 transition-colors"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
                
                                 {/* Months Grid */}
                 <div className="grid grid-cols-2 gap-2 mt-4">
                   {months.map((month, index) => (
                     <button
                       key={month}
                       onClick={() => setSelectedMonth(index)}
                                               className={`py-1 px-2 text-sm font-medium rounded border transition-colors ${
                         selectedMonth === index
                           ? 'bg-blue-600 text-white border-blue-600'
                           : 'bg-white text-blue-600 border-gray-300 hover:bg-blue-50'
                       }`}
                     >
                       {month}
                     </button>
                   ))}
                 </div>
              </div>
           </div>

           {/* Column 3: Month Section (30%) */}
           <div className="w-[30%]">
             <div className="bg-blue-600 text-white px-3 py-2 rounded-t-lg">
               <h3 className="font-semibold text-center text-sm">Month</h3>
             </div>
             <div className="border border-gray-300 rounded-b-lg p-3">
                <div className="flex items-center justify-center space-x-2 mb-3">
                 <button 
                   onClick={() => handleMonthChange('prev')}
                   className="text-blue-600 hover:text-blue-800"
                 >
                   <ChevronLeft className="h-4 w-4" />
                 </button>
                 <span className="font-medium text-gray-700 text-sm">{months[selectedMonth]}</span>
                 <button 
                   onClick={() => handleMonthChange('next')}
                   className="text-blue-600 hover:text-blue-800"
                 >
                   <ChevronRight className="h-4 w-4" />
                 </button>
               </div>

               {/* Calendar Grid */}
               <div className="border border-gray-300 rounded-lg overflow-hidden">
                                  {/* Days of Week Header */}
                  <div className="grid grid-cols-7 bg-blue-600">
                    {daysOfWeek.map(day => (
                      <div key={day} className="px-1 py-1 text-center">
                        <span className="text-xs font-medium text-white">{day}</span>
                      </div>
                    ))}
                  </div>

                  {/* Calendar Days */}
                  <div className="grid grid-cols-7">
                    {calendarDays.map((day, index) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const dayDate = new Date(day.date);
                      dayDate.setHours(0, 0, 0, 0);
                      const isPastDate = dayDate < today;
                      const isToday = dayDate.getTime() === today.getTime();
                      const isSelected = selectedDate && selectedDate.getDate() === day.day && selectedDate.getMonth() === day.date.getMonth() && selectedDate.getFullYear() === day.date.getFullYear();
                      
                      // New color logic based on slot availability
                      const hasAvailableSlots = daysWithAvailableSlots.includes(day.day) && day.isCurrentMonth;
                      const isUnavailable = unavailableDays.includes(day.day) && day.isCurrentMonth;
                      
                      return (
                        <div
                          key={index}
                          className={`px-1 py-1 text-center border-r border-b border-gray-300 min-h-[30px] flex items-center justify-center cursor-pointer transition-all duration-200 ${
                            !day.isCurrentMonth
                              ? 'bg-gray-50 text-gray-400'
                              : isPastDate
                                ? 'bg-gray-200 text-gray-500'
                                : isSelected
                                  ? 'bg-blue-600 text-white shadow-md'
                                  : hasAvailableSlots
                                    ? isToday
                                      ? 'bg-green-400 text-gray-800 shadow-md'
                                      : 'bg-green-400 text-gray-800 hover:bg-green-500 shadow-sm'
                                    : isUnavailable
                                      ? 'bg-red-400 text-gray-800 hover:bg-red-500 shadow-sm'
                                      : 'bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                          onClick={() => handleDayClick(day)}
                        >
                          <span className="text-xs font-medium">{day.day}</span>
                        </div>
                      );
                    })}
                  </div>
               </div>
               
               {/* Calendar Legend */}
               <div className="mt-3 px-2">
                 <div className="flex justify-center space-x-4 text-xs">
                   <div className="flex items-center space-x-1">
                     <div className="w-3 h-3 bg-green-400 rounded"></div>
                     <span className="text-gray-600">Available Slots</span>
                   </div>
                   <div className="flex items-center space-x-1">
                     <div className="w-3 h-3 bg-red-400 rounded"></div>
                     <span className="text-gray-600">No Available Slots</span>
                   </div>
                   <div className="flex items-center space-x-1">
                     <div className="w-3 h-3 bg-blue-600 rounded"></div>
                     <span className="text-gray-600">Selected</span>
                   </div>
                 </div>
               </div>
             </div>
           </div>

           {/* Column 4: Time & Unavailability (30%) */}
            <div className="w-[30%] space-y-3">
                         {/* Time Section */}
             <div className="bg-blue-600 text-white px-3 py-2 rounded-t-lg">
               <h3 className="font-semibold text-center text-sm">Time</h3>
             </div>
             
             {/* Working Hours Display */}
             {workingHours && (
               <div className="bg-green-50 border-l-4 border-green-400 p-2">
                 <div className="text-center">
                   <p className="text-xs text-green-800 font-medium">Working Hours:</p>
                   <p className="text-xs text-green-900 font-semibold">{workingHours}</p>
                 </div>
               </div>
             )}
             
             {/* Selected Date Info */}
             {selectedDate && (
               <div className="bg-blue-50 border-l-4 border-blue-400 p-2">
                 <p className="text-xs text-blue-800 text-center">
                   Showing availability for: <span className="font-medium">
                     {selectedDate.toLocaleDateString('en-US', { 
                       weekday: 'short', 
                       month: 'short', 
                       day: 'numeric' 
                     })}
                   </span>
                 </p>
               </div>
             )}
             
                           <div className="border border-gray-300 rounded-b-lg p-3">
                {/* Display message when no time slots or show grid */}
                {selectedTimeSlots.length === 0 ? (
                  <div className="flex items-center justify-center py-8">
                    <p className="text-gray-500 text-sm">
                      {selectedDate 
                        ? "No time slots available for the selected date" 
                        : "Please select a date to view available time slots"
                      }
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-6 gap-1.5">
                    {selectedTimeSlots.map(slot => (
                      <button
                        key={slot.id}
                        className={`h-8 flex items-center justify-center text-xs font-medium rounded cursor-pointer transition-all duration-200 transform hover:scale-105 ${
                          slot.status === 'selected'
                            ? 'bg-blue-500 text-white border border-blue-600 shadow-sm' 
                            : slot.status === 'booked'
                              ? 'bg-red-400 text-white border border-red-600 shadow-sm cursor-not-allowed'
                              : slot.status === 'unavailable'
                                ? 'bg-red-400 text-white border border-red-600 shadow-sm cursor-not-allowed'
                                : 'bg-green-400 text-white border border-green-600 shadow-sm hover:bg-green-600'
                        }`}
                        onClick={() => handleTimeSlotToggle(slot.id)}
                        disabled={slot.status === 'booked' || slot.status === 'unavailable'}
                      >
                        {slot.start}
                      </button>
                    ))}
                  </div>
                )}
                
                {/* Legend */}
                {selectedTimeSlots.length > 0 && (
                  <div className="mt-4 flex justify-center space-x-4">
                    <div className="flex items-center space-x-1">
                      <div className="w-3 h-3 bg-green-400 rounded"></div>
                      <span className="text-xs text-gray-600">Available</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <div className="w-3 h-3 bg-blue-500 rounded"></div>
                      <span className="text-xs text-gray-600">Selected</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <div className="w-3 h-3 bg-red-400 rounded"></div>
                      <span className="text-xs text-gray-600">Unavailable/Booked</span>
                    </div>
                  </div>
                )}
              </div>

            {/* Unavailability Section */}
            <div className="bg-blue-600 text-white px-3 py-2 rounded-t-lg">
              <h3 className="font-semibold text-center text-sm">Unavailability</h3>
            </div>
            
                         <div className="border border-gray-300 rounded-b-lg p-3 space-y-3">
               <div className="space-y-2">
                                   {/* First Row: Sick Leave and Annual Leave on same line */}
                  <div className="flex gap-24">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={unavailabilityReasons.sickLeave}
                        onChange={() => handleUnavailabilityChange('sickLeave')}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-xs text-gray-700">Sick Leave</span>
                    </label>
                    
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={unavailabilityReasons.annualLeave}
                        onChange={() => handleUnavailabilityChange('annualLeave')}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-xs text-gray-700">Annual Leave</span>
                    </label>
                  </div>
                 
                 {/* Second Row: Other below Sick Leave */}
                 <div>
                   <label className="flex items-center space-x-2">
                     <input
                       type="checkbox"
                       checked={unavailabilityReasons.other}
                       onChange={() => handleUnavailabilityChange('other')}
                       className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                     />
                     <span className="text-xs text-gray-700">Other (please specify)</span>
                   </label>
                 </div>
               </div>

              {unavailabilityReasons.other && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Other
                  </label>
                  <input
                    type="text"
                    value={otherReason}
                    onChange={(e) => setOtherReason(e.target.value)}
                    className="w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs"
                    placeholder="Please specify..."
                  />
                </div>
              )}

              <div className="flex justify-end">
                <button
                  onClick={handleSubmit}
                  className="bg-blue-600 text-white px-3 py-1 rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors"
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorAvailabilityPage;
